import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Grid3X3,
  List,
  Tag,
  Briefcase,
  Users,
  Star,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Eye,
  Settings,
  Download,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../contexts/AuthContext';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/api';


const IMAGE_BASE_URL = API_BASE_URL.replace('/api', '');

interface Category {
  id: number;
  name: string;
  description: string;
  icon: string;
  image?: string;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
  skills_count?: number;
  talent_count?: number;
}

interface Skill {
  id: number;
  name: string;
  description: string;
  category_id: number;
  subcategory_id?: number;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
  category_name?: string;
  category_status?: string;
  subcategory_name?: string;
  talent_count?: number;
}

interface Subcategory {
  id: number;
  name: string;
  description: string;
  category_id: number;
  icon: string;
  image?: string;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
  category_name?: string;
  skills_count?: number;
  talent_count?: number;
}

// ─── Single source of truth for image URLs ───────────────────────────────────
// Strips any trailing /api from API_BASE_URL so image paths resolve correctly.
const getImageUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path; // already absolute
  const base = API_BASE_URL.replace(/\/api\/?$/, ""); // remove trailing /api
  return `${base}${path}`;
};
// ─────────────────────────────────────────────────────────────────────────────

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState<
    "categories" | "subcategories" | "skills"
  >("categories");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubcategory, setEditingSubcategory] =
    useState<Subcategory | null>(null);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const { admin } = useAuth();

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    icon: "Briefcase",
    status: "active",
    image: "",
  });
  const [categoryImageFile, setCategoryImageFile] = useState<File | null>(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState<string>("");

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: "",
    description: "",
    category_id: 0,
    icon: "Tag",
    status: "active",
    image: "",
  });
  const [subcategoryImageFile, setSubcategoryImageFile] = useState<File | null>(
    null,
  );
  const [subcategoryImagePreview, setSubcategoryImagePreview] =
    useState<string>("");

  const [skillForm, setSkillForm] = useState({
    name: "",
    description: "",
    category_id: 0,
    subcategory_id: 0,
    status: "active",
  });

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchSkills();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get("/categories");
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await api.get("/skills");
      if (response.data.success) {
        setSkills(response.data.skills);
      }
    } catch (error) {
      console.error("Failed to fetch skills:", error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await api.get("/subcategories");
      if (response.data.success) {
        setSubcategories(response.data.subcategories);
      }
    } catch (error) {
      console.error("Failed to fetch subcategories:", error);
    }
  };

  const handleSubcategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let imageUrl = subcategoryForm.image;

      if (subcategoryImageFile) {
        const formData = new FormData();
        formData.append("image", subcategoryImageFile);
        const uploadResponse = await api.post(
          "/subcategories/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (uploadResponse.data.success) {
          imageUrl = uploadResponse.data.imagePath;
        }
      }

      const subcategoryData = { ...subcategoryForm, image: imageUrl };

      if (editingSubcategory) {
        await api.put(
          `/subcategories/${editingSubcategory.id}`,
          subcategoryData,
        );
      } else {
        await api.post("/subcategories", subcategoryData);
      }
      setShowSubcategoryModal(false);
      setEditingSubcategory(null);
      setSubcategoryForm({
        name: "",
        description: "",
        category_id: 0,
        icon: "Tag",
        status: "active",
        image: "",
      });
      setSubcategoryImageFile(null);
      setSubcategoryImagePreview("");
      fetchSubcategories();
    } catch (error) {
      console.error("Failed to save subcategory:", error);
    }
  };

  const openEditSubcategory = (subcategory: Subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      name: subcategory.name,
      description: subcategory.description,
      category_id: subcategory.category_id,
      icon: subcategory.icon,
      image: subcategory.image || "",
      status: subcategory.status,
    });
    const imgUrl = getImageUrl(subcategory.image);
    setSubcategoryImagePreview(imgUrl || "");
    setShowSubcategoryModal(true);
  };

  const handleSubcategoryImageFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setSubcategoryImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSubcategoryImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const deleteSubcategory = async (id: number) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the subcategory and all associated data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/subcategories/${id}`);
        fetchSubcategories();
        Swal.fire("Deleted!", "Subcategory has been deleted.", "success");
      } catch (error) {
        console.error("Failed to delete subcategory:", error);
        Swal.fire("Error!", "Failed to delete subcategory.", "error");
      }
    }
  };

  const toggleSubcategoryStatus = async (id: number) => {
    try {
      await api.patch(`/subcategories/${id}/toggle-status`);
      fetchSubcategories();
    } catch (error) {
      console.error("Failed to toggle subcategory status:", error);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let imageUrl = categoryForm.image;

      if (categoryImageFile) {
        const formData = new FormData();
        formData.append("image", categoryImageFile);
        const uploadResponse = await api.post(
          "/categories/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (uploadResponse.data.success) {
          imageUrl = uploadResponse.data.imagePath;
        }
      }

      const categoryData = { ...categoryForm, image: imageUrl };

      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, categoryData);
      } else {
        await api.post("/categories", categoryData);
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        description: "",
        icon: "Briefcase",
        status: "active",
        image: "",
      });
      setCategoryImageFile(null);
      setCategoryImagePreview("");
      fetchCategories();
    } catch (error) {
      console.error("Failed to save category:", error);
    }
  };

  const handleSkillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSkill) {
        await api.put(`/skills/${editingSkill.id}`, skillForm);
      } else {
        await api.post("/skills", skillForm);
      }
      setShowSkillModal(false);
      setEditingSkill(null);
      setSkillForm({
        name: "",
        description: "",
        category_id: 0,
        subcategory_id: 0,
        status: "active",
      });
      fetchSkills();
    } catch (error) {
      console.error("Failed to save skill:", error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    const result = await Swal.fire({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this category? This will also delete all associated skills.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/categories/${id}`);
        fetchCategories();
        Swal.fire({
          title: "Success!",
          text: "Category deleted successfully",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#28a745",
        });
      } catch (error) {
        console.error("Failed to delete category:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to delete category",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      }
    }
  };

  const handleDeleteSkill = async (id: number) => {
    const result = await Swal.fire({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this skill?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/skills/${id}`);
        fetchSkills();
        Swal.fire({
          title: "Success!",
          text: "Skill deleted successfully",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#28a745",
        });
      } catch (error) {
        console.error("Failed to delete skill:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to delete skill",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      }
    }
  };

  const openEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description,
      icon: category.icon,
      status: category.status,
      image: category.image || "",
    });
    setCategoryImagePreview(getImageUrl(category.image) || "");
    setCategoryImageFile(null);
    setShowCategoryModal(true);
  };

  const openEditSkill = (skill: Skill) => {
    setEditingSkill(skill);
    setSkillForm({
      name: skill.name,
      description: skill.description,
      category_id: skill.category_id,
      subcategory_id: skill.subcategory_id || 0,
      status: skill.status,
    });
    setShowSkillModal(true);
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      setCategoryImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCategoryImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setCategoryImageFile(null);
    setCategoryImagePreview("");
    setCategoryForm({ ...categoryForm, image: "" });
    const fileInput = document.getElementById(
      "categoryImageInput",
    ) as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      Briefcase,
      Users,
      Star,
      Tag,
      Settings,
      CheckCircle,
      XCircle,
      AlertCircle,
      BarChart3,
    };
    const IconComponent = icons[iconName] || Briefcase;
    return <IconComponent size={24} />;
  };

  // ─── Category image — grid card ──────────────────────────────────────────
  const getCategoryDisplayImage = (category: Category) => {
    const url = getImageUrl(category.image);
    if (url) {
      return (
        <img
          src={url}
          alt={category.name}
          style={{
            width: "50px",
            height: "50px",
            objectFit: "cover",
            borderRadius: "12px",
          }}
        />
      );
    }
    return getIconComponent(category.icon);
  };

  // ─── Category image — list view ──────────────────────────────────────────
  const getCategoryListImage = (category: Category) => {
    const url = getImageUrl(category.image);
    if (url) {
      return (
        <img
          src={url}
          alt={category.name}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      );
    }
    return getIconComponent(category.icon);
  };

  // ─── Subcategory image — grid card ───────────────────────────────────────
  const getSubcategoryDisplayImage = (
    subcategory: Subcategory,
  ): string | null => {
    return getImageUrl(subcategory.image);
  };

  // ─── Subcategory image — list view ───────────────────────────────────────
  const getSubcategoryListImage = (subcategory: Subcategory) => {
    const url = getImageUrl(subcategory.image);
    if (url) {
      return (
        <img
          src={url}
          alt={subcategory.name}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      );
    }
    return getIconComponent(subcategory.icon);
  };
  const exportCategories = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Description",
        "Status",
        "Skills Count",
        "Created At",
      ].join(","),
      ...filteredCategories.map((cat) =>
        [
          cat.id,
          `"${cat.name}"`,
          `"${cat.description}"`,
          cat.status,
          cat.skills_count || 0,
          formatDate(cat.created_at),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categories.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportSubcategories = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Description",
        "Parent Category",
        "Status",
        "Skills Count",
        "Created At",
      ].join(","),
      ...filteredSubcategories.map((sub) =>
        [
          sub.id,
          `"${sub.name}"`,
          `"${sub.description}"`,
          `"${sub.category_name || ""}"`,
          sub.status,
          sub.skills_count || 0,
          formatDate(sub.created_at),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "subcategories.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportSkills = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Description",
        "Category",
        "Subcategory",
        "Status",
        "Talent Count",
        "Created At",
      ].join(","),
      ...filteredSkills.map((skill) =>
        [
          skill.id,
          `"${skill.name}"`,
          `"${skill.description}"`,
          `"${skill.category_name || ""}"`,
          `"${skill.subcategory_name || ""}"`,
          skill.status,
          skill.talent_count || 0,
          formatDate(skill.created_at),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "skills.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      (category.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || category.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredSubcategories = subcategories.filter((subcategory) => {
    const matchesSearch =
      (subcategory.name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (subcategory.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (subcategory.category_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || subcategory.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredSkills = skills.filter((skill) => {
    const matchesSearch =
      (skill.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (skill.description || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || skill.status === filterStatus;
    const matchesCategory =
      !selectedCategory || skill.category_id === selectedCategory.id;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  const totalPages = Math.ceil(
    (activeTab === "categories"
      ? filteredCategories.length
      : activeTab === "subcategories"
        ? filteredSubcategories.length
        : filteredSkills.length) / itemsPerPage,
  );

  const currentCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const currentSubcategories = filteredSubcategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const currentSkills = filteredSkills.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getStatusBadge = (status: string) =>
    status === "active" ? "badge-success" : "badge-danger";

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  if (loading) {
    return (
      <AdminLayout
        title="Categories & Skills Management"
        subtitle="Manage service categories and skills"
      >
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  const stats = {
    totalCategories: categories.length,
    activeCategories: categories.filter((c) => c.status === "active").length,
    totalSubcategories: subcategories.length,
    activeSubcategories: subcategories.filter((sc) => sc.status === "active")
      .length,
    totalSkills: skills.length,
    activeSkills: skills.filter((s) => s.status === "active").length,
    avgSkillsPerCategory:
      categories.length > 0
        ? (skills.length / categories.length).toFixed(1)
        : "0",
  };

  return (
    <AdminLayout
      title="Categories & Skills Management"
      subtitle="Manage service categories and skills"
    >
      <div className="fade-in">
        {/* Tab Navigation */}
        <div className="admin-card" style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid var(--border-light)",
              marginBottom: "0",
            }}
          >
            {(["categories", "subcategories", "skills"] as const).map((tab) => (
              <button
                key={tab}
                className={`tab-button ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  borderBottom:
                    activeTab === tab
                      ? "2px solid var(--primary-color)"
                      : "2px solid transparent",
                  color:
                    activeTab === tab
                      ? "var(--primary-color)"
                      : "var(--text-dark-gray)",
                  fontWeight: activeTab === tab ? "600" : "400",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {tab === "categories" && <Briefcase size={18} />}
                {tab === "subcategories" && <Tag size={18} />}
                {tab === "skills" && <Star size={18} />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: "25px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flex: 1,
              }}
            >
              <div style={{ position: "relative", minWidth: "300px" }}>
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ paddingLeft: "40px" }}
                />
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-dark-gray)",
                  }}
                />
              </div>
              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ minWidth: "120px" }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div style={{ display: "flex", gap: "5px" }}>
                <button
                  className={
                    viewMode === "grid" ? "btn-primary" : "btn-secondary"
                  }
                  onClick={() => setViewMode("grid")}
                  style={{ padding: "10px", minWidth: "auto" }}
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  className={
                    viewMode === "list" ? "btn-primary" : "btn-secondary"
                  }
                  onClick={() => setViewMode("list")}
                  style={{ padding: "10px", minWidth: "auto" }}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="btn-secondary"
                onClick={
                  activeTab === "categories"
                    ? exportCategories
                    : activeTab === "subcategories"
                      ? exportSubcategories
                      : exportSkills
                }
              >
                <Download size={18} /> Export
              </button>
              {activeTab === "categories" && (
                <button
                  className="btn-primary"
                  onClick={() => setShowCategoryModal(true)}
                >
                  <Plus size={18} /> Add Category
                </button>
              )}
              {activeTab === "subcategories" && (
                <button
                  className="btn-primary"
                  onClick={() => setShowSubcategoryModal(true)}
                >
                  <Plus size={18} /> Add Subcategory
                </button>
              )}
              {activeTab === "skills" && (
                <button
                  className="btn-primary"
                  onClick={() => setShowSkillModal(true)}
                >
                  <Plus size={18} /> Add Skill
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div
          className="stat-cards start-cat-card"
          style={{ marginBottom: "25px" }}
        >
          {activeTab === "categories" && (
            <>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Tag size={24} />
                  </div>
                  <div className="stat-value">{stats.totalCategories}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Total Categories</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-blue)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeCategories} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Briefcase size={24} />
                  </div>
                  <div className="stat-value">{stats.totalSkills}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Total Skills</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-green)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeSkills} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <BarChart3 size={24} />
                  </div>
                  <div className="stat-value">{stats.avgSkillsPerCategory}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Avg Skills per Category</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-value">
                    {Math.round(
                      (stats.activeCategories / stats.totalCategories) * 100,
                    ) || 0}
                    %
                  </div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Active Rate</div>
                </div>
              </div>
            </>
          )}
          {activeTab === "subcategories" && (
            <>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Tag size={24} />
                  </div>
                  <div className="stat-value">{stats.totalSubcategories}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Total Subcategories</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-blue)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeSubcategories} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Briefcase size={24} />
                  </div>
                  <div className="stat-value">{stats.totalCategories}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Parent Categories</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-green)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeCategories} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <BarChart3 size={24} />
                  </div>
                  <div className="stat-value">
                    {categories.length > 0
                      ? (subcategories.length / categories.length).toFixed(1)
                      : "0"}
                  </div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Avg per Category</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-value">
                    {Math.round(
                      (stats.activeSubcategories / stats.totalSubcategories) *
                      100,
                    ) || 0}
                    %
                  </div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Active Rate</div>
                </div>
              </div>
            </>
          )}
          {activeTab === "skills" && (
            <>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Star size={24} />
                  </div>
                  <div className="stat-value">{stats.totalSkills}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Total Skills</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-blue)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeSkills} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Briefcase size={24} />
                  </div>
                  <div className="stat-value">{stats.totalCategories}</div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Categories</div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "var(--accent-green)",
                      marginTop: "0px",
                    }}
                  >
                    {stats.activeCategories} active
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <Users size={24} />
                  </div>
                  <div className="stat-value">
                    {skills.reduce(
                      (acc, skill) => acc + (Number(skill.talent_count) || 0),
                      0,
                    )}
                  </div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Total Talent</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="card-count">
                  <div className="stat-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-value">
                    {Math.round(
                      (stats.activeSkills / stats.totalSkills) * 100,
                    ) || 0}
                    %
                  </div>
                </div>
                <div className="card-dec">
                  <div className="stat-label">Active Rate</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── CATEGORIES ── */}
        {activeTab === "categories" && (
          <div className="admin-card" style={{ marginBottom: "25px" }}>
            <div className="card-header">
              <div className="card-title">Categories</div>
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Showing {currentCategories.length} of{" "}
                {filteredCategories.length} categories
              </div>
            </div>

            {viewMode === "grid" ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {currentCategories.map((category) => (
                  <div
                    key={category.id}
                    className="admin-card cat-crd"
                    style={{
                      margin: 0,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div
                        className="oulinr-border"
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "#fff",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#000",
                          overflow: "hidden",
                        }}
                      >
                        {getCategoryDisplayImage(category)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            marginBottom: "4px",
                          }}
                        >
                          {category.name}
                        </div>
                        <span
                          className={`badge ${getStatusBadge(category.status)}`}
                        >
                          {category.status}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "var(--text-medium-gray)",
                        marginBottom: "15px",
                        lineHeight: "1.4",
                      }}
                    >
                      {category.description}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-dark-gray)",
                        }}
                      >
                        {category.skills_count || 0} skills • Created{" "}
                        {formatDate(category.created_at)}
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: "6px", minWidth: "auto" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditCategory(category);
                          }}
                          title="Edit Category"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn-danger"
                          style={{ padding: "6px", minWidth: "auto" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }}
                          title="Delete Category"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Skills Count</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCategories.map((category) => (
                      <tr key={category.id}>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                background: "var(--accent-blue)",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                overflow: "hidden",
                              }}
                            >
                              {getCategoryListImage(category)}
                            </div>
                            <div
                              style={{ fontWeight: "500", marginBottom: "2px" }}
                            >
                              {category.name}
                            </div>
                          </div>
                        </td>
                        <td style={{ maxWidth: "300px" }}>
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {category.description}
                          </div>
                        </td>
                        <td>
                          <span
                            style={{
                              background: "var(--accent-blue)",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                            }}
                          >
                            {category.skills_count || 0}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${getStatusBadge(category.status)}`}
                          >
                            {category.status}
                          </span>
                        </td>
                        <td style={{ fontSize: "12px" }}>
                          {formatDate(category.created_at)}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              className="btn-secondary"
                              style={{ padding: "6px", minWidth: "auto" }}
                              onClick={() => setSelectedCategory(category)}
                              title="View Skills"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              className="btn-secondary"
                              style={{ padding: "6px", minWidth: "auto" }}
                              onClick={() => openEditCategory(category)}
                              title="Edit Category"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn-danger"
                              style={{ padding: "6px", minWidth: "auto" }}
                              onClick={() => handleDeleteCategory(category.id)}
                              title="Delete Category"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                padding: "20px 0",
              }}
            >
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Previous
                </button>
                <button
                  className="btn-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SUBCATEGORIES ── */}
        {activeTab === "subcategories" && (
          <div className="admin-card" style={{ marginBottom: "25px" }}>
            <div className="card-header">
              <div className="card-title">Subcategories</div>
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Showing {currentSubcategories.length} of{" "}
                {filteredSubcategories.length} subcategories
              </div>
            </div>

            {viewMode === "grid" ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {currentSubcategories.map((subcategory) => {
                  const subImgUrl = getSubcategoryDisplayImage(subcategory);
                  return (
                    <div
                      key={subcategory.id}
                      className="admin-card cat-crd"
                      style={{
                        margin: 0,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          className="oulinr-border"
                          style={{
                            width: "50px",
                            height: "50px",
                            background: "#fff",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#000",
                            overflow: "hidden",
                          }}
                        >
                          {subImgUrl ? (
                            <img
                              src={subImgUrl}
                              alt={subcategory.name}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: "12px",
                              }}
                            />
                          ) : (
                            getIconComponent(subcategory.icon)
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "16px",
                              marginBottom: "4px",
                            }}
                          >
                            {subcategory.name}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--text-medium-gray)",
                              marginBottom: "4px",
                            }}
                          >
                            {subcategory.category_name}
                          </div>
                          <span
                            className={`badge ${getStatusBadge(subcategory.status)}`}
                          >
                            {subcategory.status}
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "var(--text-medium-gray)",
                          marginBottom: "15px",
                          lineHeight: "1.4",
                        }}
                      >
                        {subcategory.description}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-dark-gray)",
                          }}
                        >
                          {subcategory.skills_count || 0} skills • Created{" "}
                          {formatDate(subcategory.created_at)}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditSubcategory(subcategory);
                            }}
                            title="Edit Subcategory"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="btn-danger"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSubcategory(subcategory.id);
                            }}
                            title="Delete Subcategory"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Subcategory</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Skills Count</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSubcategories.map((subcategory) => (
                      <tr key={subcategory.id}>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                background: "var(--accent-blue)",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                overflow: "hidden",
                              }}
                            >
                              {getSubcategoryListImage(subcategory)}
                            </div>
                            <div
                              style={{ fontWeight: "500", marginBottom: "2px" }}
                            >
                              {subcategory.name}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            style={{
                              background: "var(--accent-purple)",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                            }}
                          >
                            {subcategory.category_name}
                          </span>
                        </td>
                        <td style={{ maxWidth: "300px" }}>
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {subcategory.description}
                          </div>
                        </td>
                        <td>
                          <span
                            style={{
                              background: "var(--accent-blue)",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                            }}
                          >
                            {subcategory.skills_count || 0}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${getStatusBadge(subcategory.status)}`}
                          >
                            {subcategory.status}
                          </span>
                        </td>
                        <td style={{ fontSize: "12px" }}>
                          {formatDate(subcategory.created_at)}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              className="btn-secondary"
                              style={{ padding: "6px", minWidth: "auto" }}
                              onClick={() => openEditSubcategory(subcategory)}
                              title="Edit Subcategory"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn-danger"
                              style={{ padding: "6px", minWidth: "auto" }}
                              onClick={() => deleteSubcategory(subcategory.id)}
                              title="Delete Subcategory"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                padding: "20px 0",
              }}
            >
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Previous
                </button>
                <button
                  className="btn-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SKILLS ── */}
        {activeTab === "skills" && (
          <div className="admin-card" style={{ marginBottom: "25px" }}>
            <div className="card-header">
              <div className="card-title">Skills</div>
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Showing {currentSkills.length} of {filteredSkills.length} skills
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Skill Name</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Subcategory</th>
                    <th>Status</th>
                    <th>Talent Count</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSkills.map((skill) => (
                    <tr key={skill.id}>
                      <td>
                        <div style={{ fontWeight: "500" }}>{skill.name}</div>
                      </td>
                      <td style={{ maxWidth: "300px" }}>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {skill.description}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "var(--accent-purple)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                          }}
                        >
                          {skill.category_name}
                        </span>
                      </td>
                      <td>
                        {skill.subcategory_name ? (
                          <span
                            style={{
                              background: "var(--accent-blue)",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                            }}
                          >
                            {skill.subcategory_name}
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "var(--text-medium-gray)",
                              fontSize: "12px",
                            }}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${getStatusBadge(skill.status)}`}
                        >
                          {skill.status}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "var(--accent-green)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                          }}
                        >
                          {skill.talent_count || 0}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {formatDate(skill.created_at)}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={() => openEditSkill(skill)}
                            title="Edit Skill"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="btn-danger"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={() => handleDeleteSkill(skill.id)}
                            title="Delete Skill"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                padding: "20px 0",
              }}
            >
              <div
                style={{ fontSize: "14px", color: "var(--text-medium-gray)" }}
              >
                Page {currentPage} of {totalPages}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Previous
                </button>
                <button
                  className="btn-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SELECTED CATEGORY SKILLS PANEL ── */}
        {selectedCategory && activeTab === "categories" && (
          <div className="admin-card">
            <div className="card-header">
              {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
                <div>
                  <div className="card-title">Skills in {selectedCategory.name}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>{filteredSkills.length} skills found</div>
                </div>
                <button className="btn-secondary" onClick={() => setSelectedCategory(null)}><XCircle size={18} /> Close</button>
              {/* </div> */}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Skill Name</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Talent Count</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkills.map((skill) => (
                    <tr key={skill.id}>
                      <td>
                        <div style={{ fontWeight: "500" }}>{skill.name}</div>
                      </td>
                      <td style={{ maxWidth: "300px" }}>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {skill.description}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "var(--accent-purple)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                          }}
                        >
                          {skill.category_name}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${getStatusBadge(skill.status)}`}
                        >
                          {skill.status}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "var(--accent-green)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                          }}
                        >
                          {skill.talent_count || 0}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {formatDate(skill.created_at)}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={() => openEditSkill(skill)}
                            title="Edit Skill"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="btn-danger"
                            style={{ padding: "6px", minWidth: "auto" }}
                            onClick={() => handleDeleteSkill(skill.id)}
                            title="Delete Skill"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── CATEGORY MODAL ── */}
        {showCategoryModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              className="admin-card"
              style={{
                width: "90%",
                maxWidth: "500px",
                margin: 0,
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div className="card-header">
                <div className="card-title">
                  {editingCategory ? "Edit Category" : "Add New Category"}
                </div>
              </div>
              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Category Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category Image</label>
                  <div style={{ marginBottom: "10px" }}>
                    <input
                      id="categoryImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        document.getElementById("categoryImageInput")?.click()
                      }
                      style={{ marginRight: "10px" }}
                    >
                      Choose Image
                    </button>
                    {(categoryImagePreview || categoryForm.image) && (
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={removeImage}
                        style={{ padding: "8px 12px" }}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                  {(categoryImagePreview || categoryForm.image) && (
                    <div
                      style={{
                        marginTop: "10px",
                        border: "2px dashed #ddd",
                        borderRadius: "8px",
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={
                          categoryImagePreview ||
                          getImageUrl(categoryForm.image) ||
                          ""
                        }
                        alt="Category preview"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "150px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-medium-gray)",
                          marginTop: "5px",
                        }}
                      >
                        Image Preview
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-medium-gray)",
                      marginTop: "5px",
                    }}
                  >
                    Recommended: Square image, max 5MB (JPG, PNG, GIF)
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Icon (Fallback)</label>
                  <select
                    className="form-control"
                    value={categoryForm.icon}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, icon: e.target.value })
                    }
                  >
                    <option value="Briefcase">Briefcase</option>
                    <option value="Users">Users</option>
                    <option value="Star">Star</option>
                    <option value="Tag">Tag</option>
                    <option value="Settings">Settings</option>
                  </select>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-medium-gray)",
                      marginTop: "5px",
                    }}
                  >
                    This icon will be used if no image is uploaded
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={categoryForm.status}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        status: e.target.value as "active" | "inactive",
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowCategoryModal(false);
                      setEditingCategory(null);
                      setCategoryForm({
                        name: "",
                        description: "",
                        icon: "Briefcase",
                        status: "active",
                        image: "",
                      });
                      setCategoryImageFile(null);
                      setCategoryImagePreview("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingCategory ? "Update" : "Create"} Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── SKILL MODAL ── */}
        {showSkillModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="admin-card"
              style={{
                width: "90%",
                maxWidth: "500px",
                margin: 0,
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div className="card-header">
                <div className="card-title">
                  {editingSkill ? "Edit Skill" : "Add New Skill"}
                </div>
              </div>
              <form onSubmit={handleSkillSubmit}>
                <div className="form-group">
                  <label className="form-label">Skill Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={skillForm.name}
                    onChange={(e) =>
                      setSkillForm({ ...skillForm, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={skillForm.description}
                    onChange={(e) =>
                      setSkillForm({
                        ...skillForm,
                        description: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-control"
                    value={skillForm.category_id}
                    onChange={(e) =>
                      setSkillForm({
                        ...skillForm,
                        category_id: parseInt(e.target.value),
                        subcategory_id: 0,
                      })
                    }
                    required
                  >
                    <option value={0}>Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subcategory (Optional)</label>
                  <select
                    className="form-control"
                    value={skillForm.subcategory_id}
                    onChange={(e) =>
                      setSkillForm({
                        ...skillForm,
                        subcategory_id: parseInt(e.target.value),
                      })
                    }
                  >
                    <option value={0}>No subcategory</option>
                    {subcategories
                      .filter(
                        (sub) =>
                          sub.category_id === skillForm.category_id &&
                          sub.status === "active",
                      )
                      .map((subcategory) => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                  </select>
                  {skillForm.category_id === 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-medium-gray)",
                        marginTop: "5px",
                      }}
                    >
                      Select a category first to see available subcategories
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={skillForm.status}
                    onChange={(e) =>
                      setSkillForm({
                        ...skillForm,
                        status: e.target.value as "active" | "inactive",
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowSkillModal(false);
                      setEditingSkill(null);
                      setSkillForm({
                        name: "",
                        description: "",
                        category_id: 0,
                        subcategory_id: 0,
                        status: "active",
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingSkill ? "Update" : "Create"} Skill
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── SUBCATEGORY MODAL ── */}
        {showSubcategoryModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="admin-card"
              style={{
                width: "90%",
                maxWidth: "500px",
                margin: 0,
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div className="card-header">
                <div className="card-title">
                  {editingSubcategory
                    ? "Edit Subcategory"
                    : "Add New Subcategory"}
                </div>
              </div>
              <form onSubmit={handleSubcategorySubmit}>
                <div className="form-group">
                  <label className="form-label">Subcategory Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={subcategoryForm.name}
                    onChange={(e) =>
                      setSubcategoryForm({
                        ...subcategoryForm,
                        name: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={subcategoryForm.description}
                    onChange={(e) =>
                      setSubcategoryForm({
                        ...subcategoryForm,
                        description: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Parent Category</label>
                  <select
                    className="form-control"
                    value={subcategoryForm.category_id}
                    onChange={(e) =>
                      setSubcategoryForm({
                        ...subcategoryForm,
                        category_id: parseInt(e.target.value),
                      })
                    }
                    required
                  >
                    <option value={0}>Select a category</option>
                    {categories
                      .filter((cat) => cat.status === "active")
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subcategory Image</label>
                  <div style={{ marginBottom: "10px" }}>
                    <input
                      id="subcategoryImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handleSubcategoryImageFileChange}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() =>
                        document
                          .getElementById("subcategoryImageInput")
                          ?.click()
                      }
                      style={{ marginRight: "10px" }}
                    >
                      Choose Image
                    </button>
                    {(subcategoryImagePreview || subcategoryForm.image) && (
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => {
                          setSubcategoryImageFile(null);
                          setSubcategoryImagePreview("");
                          setSubcategoryForm({ ...subcategoryForm, image: "" });
                        }}
                        style={{ padding: "8px 12px" }}
                      >
                        Remove Image
                      </button>
                    )}
                  </div>
                  {(subcategoryImagePreview || subcategoryForm.image) && (
                    <div
                      style={{
                        marginTop: "10px",
                        border: "2px dashed #ddd",
                        borderRadius: "8px",
                        padding: "10px",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={
                          subcategoryImagePreview ||
                          getImageUrl(subcategoryForm.image) ||
                          ""
                        }
                        alt="Subcategory preview"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "150px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-medium-gray)",
                          marginTop: "5px",
                        }}
                      >
                        Image Preview
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-medium-gray)",
                      marginTop: "5px",
                    }}
                  >
                    Recommended: Square image, max 5MB (JPG, PNG, GIF)
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Icon (Fallback)</label>
                  <select
                    className="form-control"
                    value={subcategoryForm.icon}
                    onChange={(e) =>
                      setSubcategoryForm({
                        ...subcategoryForm,
                        icon: e.target.value,
                      })
                    }
                  >
                    <option value="Tag">Tag</option>
                    <option value="Briefcase">Briefcase</option>
                    <option value="Users">Users</option>
                    <option value="Star">Star</option>
                    <option value="Settings">Settings</option>
                  </select>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-medium-gray)",
                      marginTop: "5px",
                    }}
                  >
                    This icon will be used if no image is uploaded
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={subcategoryForm.status}
                    onChange={(e) =>
                      setSubcategoryForm({
                        ...subcategoryForm,
                        status: e.target.value as "active" | "inactive",
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowSubcategoryModal(false);
                      setEditingSubcategory(null);
                      setSubcategoryForm({
                        name: "",
                        description: "",
                        category_id: 0,
                        icon: "Tag",
                        status: "active",
                        image: "",
                      });
                      setSubcategoryImageFile(null);
                      setSubcategoryImagePreview("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingSubcategory ? "Update" : "Create"} Subcategory
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Categories;
